import React, { useContext, useEffect, useState } from "react";
import * as Constants from "../../constants";
import { ShareContext } from "../../context/ShareContext";
import BackLoader from "../../components/backloader";
import JisButton from "../../components/controls/jisButton";
import JisDialogForm from "../../components/formdialog";
import JisEmptyList from "../../components/controls/emptylist";
import Pagenated from "../../components/pagenated";
import PdfViewer from "../../components/controls/pdfviewer";
import { api, docLink, fmtDate, tone } from "./hctc_common";
import "./hctc.css";

// Admin verification of applicant media instruments (screen media_verify).
const STATUSES = ["Pending Verification", "Active", "Rejected", "Suspended", "Expired", "all"];

export default function MediaVerification() {
  const { token, setIsExpired } = useContext(ShareContext);
  const [status, setStatus] = useState("Pending Verification");
  const [text, setText] = useState("");
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(Constants.PAGESIZE);
  const [dlg, setDlg] = useState(null);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = async (st = status, p = pageIndex, s = pageSize) => {
    setLoading(true);
    const d = await api(token, setIsExpired, `mediainstrument/verification/${p}/${s}`, { method: "POST", body: { status: st, text: text || null } });
    setLoading(false);
    if (d.code === 0) {
      setItems(d.items || []);
      setCount(d.totalCount || 0);
    } else if (d.code !== -10) setErr(d.status || "Unable to load");
  };

  useEffect(() => {
    load(status, 0, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decide = async (action, comment) => {
    setErr("");
    setLoading(true);
    const d = await api(token, setIsExpired, `mediainstrument/decide/${dlg.item.instrument.id}`, { method: "POST", body: { action, comment } });
    setLoading(false);
    if (d.code === 0) {
      setDlg(null);
      setMsg(`Instrument ${d.status}`);
      load();
    } else if (d.code !== -10) setErr(d.status || "Failed");
  };

  return (
    <div className="hctc-page">
      <h2>Media Instrument Verification</h2>
      <div className="hctc-sub">Check the instrument against the supporting document and the owner's details before approving.</div>
      <div className="hctc-chips">
        {STATUSES.map((s) => (
          <button
            key={s}
            className={`hctc-chip ${status === s ? "active" : ""}`}
            onClick={() => {
              setStatus(s);
              setPageIndex(0);
              load(s, 0, pageSize);
            }}
          >
            {s === "all" ? "All" : s}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, maxWidth: 520 }}>
        <input style={{ flex: 1, padding: 8, borderRadius: 8 }} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(status, 0, pageSize)} placeholder="owner, instrument name or number" />
        <JisButton onClick={() => load(status, 0, pageSize)}>Search</JisButton>
      </div>
      {msg && <div className="hctc-ok">{msg}</div>}
      {err && <div className="hctc-err">{err}</div>}
      <div className="table">
        <div className="table-wrapper" style={{ overflowX: "auto" }}>
          <table className="case-table hctc-table">
            <thead>
              <tr>
                <th>Instrument</th>
                <th>Owner</th>
                <th>Number / authority</th>
                <th>Validity</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map(({ instrument: i, owner: o }) => (
                <tr key={i.id}>
                  <td>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {/\.(png|jpe?g)$/i.test(i.url) && <img className="hctc-thumb" src={docLink(i.url)} alt="" />}
                      <div>
                        {i.instrumentName}
                        <div className="hctc-muted">{i.typeTitle}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {o.name}
                    <div className="hctc-muted">
                      {o.userName} {o.barNumber && `· ${o.barNumber}`}
                    </div>
                  </td>
                  <td>
                    {i.instrumentNumber || "-"}
                    <div className="hctc-muted">{i.issuingAuthority}</div>
                  </td>
                  <td>
                    {fmtDate(i.effectiveDate) || "-"} to {fmtDate(i.expiryDate) || "-"}
                  </td>
                  <td>
                    <span className={`hctc-pill ${tone(i.status)}`}>{i.status}</span>
                  </td>
                  <td>
                    <JisButton variant="plain" onClick={() => setDlg({ item: { instrument: i, owner: o } })}>
                      Review
                    </JisButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && items.length === 0 && <JisEmptyList title="Nothing to review" />}
        </div>
        {count > 0 && (
          <Pagenated
            onChangePageSize={(k) => {
              setPageSize(Number(k));
              setPageIndex(0);
              load(status, 0, Number(k));
            }}
            size={pageSize}
            total={count}
            maxPage={20}
            onPick={(p) => {
              setPageIndex(p.id - 1);
              load(status, p.id - 1, pageSize);
            }}
          />
        )}
      </div>
      {dlg && <ReviewDialog item={dlg.item} onClose={() => setDlg(null)} onDecide={decide} onPreview={setPreview} />}
      {preview && (
        <JisDialogForm title="Document" onClose={() => setPreview("")}>
          <div style={{ height: "75vh", minWidth: "min(800px, 85vw)" }}>
            {/\.(png|jpe?g)$/i.test(preview) ? <img src={preview} alt="" style={{ maxWidth: "100%", maxHeight: "100%", display: "block", margin: "auto", background: "#fff" }} /> : <PdfViewer pdfUrl={preview} />}
          </div>
        </JisDialogForm>
      )}
      <BackLoader open={loading} />
    </div>
  );
}

function ReviewDialog({ item, onClose, onDecide, onPreview }) {
  const { instrument: i, owner: o } = item;
  const [comment, setComment] = useState("");
  return (
    <JisDialogForm title={`Review: ${i.instrumentName}`} onClose={onClose} minWidth="45%">
      <div className="hctc-dialog-body">
        <div className="hctc-kv">
          <div className="k">Type</div>
          <div className="v">{i.typeTitle}</div>
          <div className="k">Owner</div>
          <div className="v">
            {o.name} ({o.userName}) {o.primaryEmail}
          </div>
          <div className="k">Enrollment / bar no.</div>
          <div className="v">{o.barNumber || "-"}</div>
          <div className="k">Number</div>
          <div className="v">{i.instrumentNumber || "-"}</div>
          <div className="k">Issuing authority</div>
          <div className="v">{i.issuingAuthority || "-"}</div>
          <div className="k">Validity</div>
          <div className="v">
            {fmtDate(i.effectiveDate) || "-"} to {fmtDate(i.expiryDate) || "-"}
          </div>
          <div className="k">File hash</div>
          <div className="v" style={{ fontSize: 11 }}>
            {i.fileHash}
          </div>
        </div>
        <div className="hctc-actions" style={{ margin: "12px 0" }}>
          <JisButton variant="outline" onClick={() => onPreview(docLink(i.url))}>
            View instrument
          </JisButton>
          {i.supportUrl && (
            <JisButton variant="outline" onClick={() => onPreview(docLink(i.supportUrl))}>
              View supporting document
            </JisButton>
          )}
        </div>
        <div className="hctc-field">
          <label>Comment (required to reject or suspend)</label>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
        <div className="hctc-actions" style={{ marginTop: 12 }}>
          {i.status === "Pending Verification" && <JisButton onClick={() => onDecide("approve", comment)}>Approve</JisButton>}
          {i.status === "Pending Verification" && (
            <JisButton variant="outline" disabled={!comment.trim()} onClick={() => onDecide("reject", comment)}>
              Reject
            </JisButton>
          )}
          {i.status === "Active" && (
            <JisButton variant="outline" disabled={!comment.trim()} onClick={() => onDecide("suspend", comment)}>
              Suspend
            </JisButton>
          )}
          {i.status === "Suspended" && <JisButton onClick={() => onDecide("reinstate", comment)}>Reinstate</JisButton>}
          <JisButton variant="plain" onClick={onClose}>
            Close
          </JisButton>
        </div>
      </div>
    </JisDialogForm>
  );
}
